import React, { useState } from "react";

/**
 * CRA env vars MUST start with REACT_APP_
 * .env example:
 * REACT_APP_CONTACT_API_URL=https://3h1asd6g.execute-api.us-east-1.amazonaws.com/prod
 * REACT_APP_MY_API_KEY=xxxxxx
 */
const CONTACT_API_URL = process.env.REACT_APP_CONTACT_API_URL || "";
const API_KEY = process.env.REACT_APP_MY_API_KEY || "";

export default function GetStartedContact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [toast, setToast] = useState({ show: false, text: "", type: "ok" }); // ok | err

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || cooldown) return;

    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();

    if (!CONTACT_API_URL) {
      setToast({
        show: true,
        text: "Missing REACT_APP_CONTACT_API_URL in .env",
        type: "err",
      });
      setTimeout(() => setToast((t) => ({ ...t, show: false })), 3500);
      return;
    }

    if (!API_KEY) {
      setToast({
        show: true,
        text: "Missing REACT_APP_MY_API_KEY in .env",
        type: "err",
      });
      setTimeout(() => setToast((t) => ({ ...t, show: false })), 3500);
      return;
    }

    setSubmitting(true);

    try {
      // Build query params as you requested
      const params = new URLSearchParams({
        name,
        email,
        message,
      });

      const apiUrl = `${CONTACT_API_URL}?${params.toString()}`;

      // POST call with x-api-key
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "x-api-key": API_KEY,
          // If your API requires JSON header/body, keep Content-Type + body
          "Content-Type": "application/json",
        },
        // Optional: also send JSON body (safe even if API reads from query)
        body: JSON.stringify({ name, email, message }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Request failed");

      setForm({ name: "", email: "", message: "" });
      setToast({ show: true, text: "Message has been sent.", type: "ok" });

      setCooldown(true);
      setTimeout(() => setCooldown(false), 10000); // 10s cooldown
    } catch (err) {
      setToast({
        show: true,
        text: err?.message || "Something went wrong. Try again.",
        type: "err",
      });
    } finally {
      setSubmitting(false);
      setTimeout(() => setToast((t) => ({ ...t, show: false })), 3000);
    }
  };

  const disabled = submitting || cooldown;

  return (
    <section className="relative flex items-center pb-[100px]">
      <div className="max-w-4xl mx-auto w-full flex flex-col md:flex-row gap-10 md:justify-between">
        {/* LEFT COPY */}
        <div className="flex flex-col justify-center">
          <div className="font-bebas font-normal tracking-wider text-[30px] lg:text-[50px] leading-[130%] text-[#06382f] mb-4">
            Get Started
          </div>
          <div className="text-[#004D4D] text-base lg:text-lg font-inter font-normal leading-[100%]">
            Tell us about your interest and we will reach out to you.
          </div>
        </div>

        {/* RIGHT FORM */}
        <form onSubmit={onSubmit} className="flex flex-col gap-8">
          <div>
            <label className="block text-[#06382f] text-lg font-inter font-semibold leading-[100%] mb-2">
              Name
            </label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={onChange}
              required
              className="w-full text-[#004D4D] font-inter font-medium text-sm leading-[100%] bg-transparent border-b-2 border-[#0b5453] focus:border-[#0b5453] outline-none py-2"
            />
          </div>

          <div>
            <label className="block text-[#06382f] text-lg font-inter font-semibold leading-[100%] mb-2">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={onChange}
              required
              className="w-full text-[#004D4D] font-inter font-medium text-sm leading-[100%] bg-transparent border-b-2 border-[#0b5453] focus:border-[#0b5453] outline-none py-2"
            />
          </div>

          <div>
            <label className="block text-[#06382f] text-lg font-inter font-semibold leading-[100%] mb-2">
              Message
            </label>
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              required
              rows={12}
              className="w-full text-[#004D4D] font-inter font-medium text-sm leading-[130%] bg-transparent border-b-2 border-[#0b5453] focus:border-[#0b5453] outline-none py-2 resize-none"
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={disabled}
              className={`w-full md:w-[340px] h-12 rounded-md bg-[#06382f] text-white font-inter text-sm font-semibold leading-[100%] transition cursor-pointer ${
                disabled ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
              }`}
            >
              {submitting
                ? "Sending..."
                : cooldown
                  ? "Please wait..."
                  : "SUBMIT"}
            </button>
          </div>
        </form>
      </div>

      {/* Toast */}
      {toast.show && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" />
          <div
            className={`relative z-10 w-full max-w-sm rounded-lg px-5 py-4 shadow-lg ${
              toast.type === "ok"
                ? "bg-white text-[#0b3c3a]"
                : "bg-white text-red-700"
            }`}
          >
            <p className="font-medium">{toast.text}</p>
          </div>
        </div>
      )}
    </section>
  );
}
